import React, { useState, useRef, useEffect } from "react";
import { t as T } from "i18next";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Heart, MessageCircle, Globe, Users, Lock, Trash2, MoreHorizontal,
  Pencil, Flag, MessageSquareOff, MessageSquare, Sparkles, Percent, Gift, ExternalLink,
  Pin, PinOff,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import PostMediaGallery from "./PostMediaGallery";
import api from "../../lib/api";
import { toast } from "sonner";
import PostComposer from "./PostComposer";
import LinkifyText from "./LinkifyText";

const VIS_ICON = { public: Globe, followers: Users, friends: Heart, private: Lock };

// 6 reaksjoner (Facebook-standard)
export const REACTIONS = [
  { kind: "heart", emoji: "❤️", label: "Hjerte" },
  { kind: "like",  emoji: "👍", label: "Liker" },
  { kind: "haha",  emoji: "😂", label: "Haha"  },
  { kind: "wow",   emoji: "😮", label: "Oi"    },
  { kind: "sad",   emoji: "😢", label: "Trist" },
  { kind: "angry", emoji: "😡", label: "Sinna" },
];
const REACTION_MAP = Object.fromEntries(REACTIONS.map((r) => [r.kind, r]));

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "nå";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}t`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short" });
}

export default function PostCard({ post: initialPost, onDeleted, onUpdated, onPinned, pinnable = false, hideMedia = false, threadMode = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [post, setPost] = useState(initialPost);
  const { id, author, text, media, visibility, comments_count,
          comments_disabled, my_reaction, reactions_count, created_at, edited, promo } = post;
  const Vis = VIS_ICON[visibility] || Globe;
  const isMine = user?.id === author?.id;
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const [showReactPicker, setShowReactPicker] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [editing, setEditing] = useState(false);
  const pickerTimer = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => { setPost(initialPost); }, [initialPost]);

  // Lukk meny ved klikk utenfor
  useEffect(() => {
    if (!showMenu) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showMenu]);

  const setMy = (kind, delta = 0) => {
    setPost((p) => ({
      ...p,
      my_reaction: kind,
      reactions_count: Math.max(0, (p.reactions_count || 0) + delta),
    }));
  };

  const react = async (kind) => {
    setShowReactPicker(false);
    const prev = my_reaction;
    const delta = prev ? 0 : 1;
    setMy(kind, delta);
    try {
      const r = await api.post(`/profile-posts/${id}/react`, { kind });
      setPost((p) => ({ ...p, my_reaction: r.data.my_reaction, reactions_count: r.data.reactions_count }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ui.react_failed"));
      setMy(prev, -delta);
    }
  };

  const unreact = async () => {
    const prev = my_reaction;
    setMy(null, -1);
    try {
      const r = await api.delete(`/profile-posts/${id}/react`);
      setPost((p) => ({ ...p, my_reaction: null, reactions_count: r.data.reactions_count }));
    } catch {
      toast.error(T("ui.reaction_remove_failed"));
      setMy(prev, 1);
    }
  };

  const onHeartClick = () => {
    if (my_reaction) unreact();
    else react("heart");
  };

  // Hover/press-til-vis-picker
  const openPicker = () => {
    clearTimeout(pickerTimer.current);
    setShowReactPicker(true);
  };
  const scheduleClose = () => {
    clearTimeout(pickerTimer.current);
    pickerTimer.current = setTimeout(() => setShowReactPicker(false), 400);
  };
  // Long-press for mobil (500ms)
  const pressTimer = useRef(null);
  const onPressStart = () => {
    pressTimer.current = setTimeout(() => setShowReactPicker(true), 500);
  };
  const onPressEnd = () => clearTimeout(pressTimer.current);

  const [delOpen, setDelOpen] = useState(false);
  const del = async (params) => {
    const linked = (media || []).length > 0;
    if (!params && linked) { setDelOpen(true); return; }
    if (!params && !window.confirm("Slette denne posten?")) return;
    try {
      await api.delete(`/profile-posts/${id}`, { params });
      setDelOpen(false);
      toast.success(T("ui.deleted"));
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ui.delete_failed"));
    }
  };

  const toggleComments = async () => {
    try {
      await api.patch(`/profile-posts/${id}`, { comments_disabled: !comments_disabled });
      setPost((p) => ({ ...p, comments_disabled: !p.comments_disabled }));
      toast.success(!comments_disabled ? "Kommentarer slått av." : "Kommentarer slått på.");
      setShowMenu(false);
    } catch {
      toast.error(T("ui.change_failed"));
    }
  };

  const togglePin = async () => {
    const pin = !post.pinned_at;
    try {
      const r = await api.post(`/profile-posts/${id}/pin`, { pinned: pin });
      setPost((p) => ({ ...p, pinned_at: r.data?.pinned_at || null }));
      toast.success(pin ? t("post.pinned_ok", "Innlegget er festet øverst.") : t("post.unpinned_ok", "Innlegget er løsnet."));
      setShowMenu(false);
      onPinned && onPinned();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t("post.pin_limit", "Du kan feste maks 3 innlegg. Løsne ett først.") : t("post.pin_failed", "Kunne ikke endre festing."));
    }
  };

  const report = async () => {
    const reason = window.prompt("Hvorfor rapporterer du denne posten? (f.eks. spam, hets, upassende innhold)");
    if (!reason || !reason.trim()) return;
    try {
      await api.post("/reports", { target_type: "profile_post", target_id: id, reason: reason.trim(), details: "" });
      toast.success(T("ui.report_received"));
      setShowMenu(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ui.report_failed"));
    }
  };

  const myReactionObj = my_reaction ? REACTION_MAP[my_reaction] : null;

  if (editing) {
    return (
      <PostComposer
        editPost={post}
        onCancel={() => setEditing(false)}
        onPosted={(updated) => {
          setEditing(false);
          if (updated) setPost((p) => ({ ...p, ...updated }));
          onUpdated?.();
        }}
      />
    );
  }

  return (
    <article className="panel-tight p-4 mb-3" data-testid={`post-card-${id}`}>
      {pinnable && post.pinned_at && (
        <div className="flex items-center gap-1 mb-2 font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--magenta)]" data-testid={`post-pinned-badge-${id}`}>
          <Pin className="h-3 w-3" /> {t("post.pinned", "Festet")}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        {author?.avatar_url ? (
          <img src={author.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-[var(--magenta)]/30 flex items-center justify-center text-xs font-bold text-white">
            {(author?.username || "?").slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <Link to={author?.pool === "creator" ? `/skaper/${author?.username}` : `/profile/${author?.username}`} className="font-fraunces italic text-[var(--cream)] hover:text-[var(--magenta)]" data-testid={`post-author-link-${id}`}>
            {author?.display_name || author?.username}
          </Link>
          <div className="font-meta text-[10px] text-[var(--cream-faint)] uppercase tracking-wider flex items-center gap-1">
            @{author?.username} · {relTime(created_at)}
            {edited && <span className="italic normal-case tracking-normal text-[var(--cream-faint)]/70">{T("ui.edited")}</span>}
            <Vis className="h-3 w-3 ml-1" />
          </div>
        </div>

        {/* Meny-knapp (3 prikker) */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu((v) => !v)}
            className="text-[var(--cream-faint)] hover:text-[var(--cream)] p-1"
            data-testid={`post-menu-${id}`}
            aria-label="Mer"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-7 z-20 bg-[#1a1618] border border-white/10 rounded-md shadow-lg py-1 min-w-[180px]">
              {isMine && (
                <>
                  <button
                    onClick={() => { setEditing(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-[var(--cream)] hover:bg-white/5 flex items-center gap-2"
                    data-testid={`post-edit-${id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" /> {t("post.edit", "Rediger")}
                  </button>
                  <button
                    onClick={toggleComments}
                    className="w-full text-left px-3 py-1.5 text-xs text-[var(--cream)] hover:bg-white/5 flex items-center gap-2"
                    data-testid={`post-toggle-comments-${id}`}
                  >
                    {comments_disabled
                      ? <><MessageSquare className="h-3.5 w-3.5" /> {t("post.enable_comments", "Slå på kommentarer")}</>
                      : <><MessageSquareOff className="h-3.5 w-3.5" /> {t("post.disable_comments", "Slå av kommentarer")}</>
                    }
                  </button>
                  <button
                    onClick={togglePin}
                    className="w-full text-left px-3 py-1.5 text-xs text-[var(--cream)] hover:bg-white/5 flex items-center gap-2"
                    data-testid={`post-pin-${id}`}
                  >
                    {post.pinned_at
                      ? <><PinOff className="h-3.5 w-3.5" /> {t("post.unpin", "Løsne")}</>
                      : <><Pin className="h-3.5 w-3.5" /> {t("post.pin", "Fest øverst")}</>}
                  </button>
                  <button
                    onClick={() => del()}
                    className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                    data-testid={`post-delete-${id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t("post.delete", "Slett")}
                  </button>
                </>
              )}
              {!isMine && (
                <button
                  onClick={report}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--cream)] hover:bg-white/5 flex items-center gap-2"
                  data-testid={`post-report-${id}`}
                >
                  <Flag className="h-3.5 w-3.5" /> {t("post.report", "Rapporter")}
                </button>
              )}
              {isAdmin && !isMine && post.pinned_at && (
                <button
                  onClick={togglePin}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--cream)] hover:bg-white/5 flex items-center gap-2"
                  data-testid={`post-admin-unpin-${id}`}
                >
                  <PinOff className="h-3.5 w-3.5" /> {t("post.admin_unpin", "Løsne (admin)")}
                </button>
              )}
              {isAdmin && !isMine && (
                <button
                  onClick={() => del()}
                  className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                  data-testid={`post-admin-delete-${id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> {t("post.admin_delete", "Slett (admin)")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tekst */}
      {text && (
        <p className="text-sm text-[var(--cream)] mb-2 font-manrope">
          <LinkifyText text={text} />
        </p>
      )}

      {/* Promo-badge (hvis posten har et tilbud) */}
      {promo && !hideMedia && <PromoBadge promo={promo} />}

      {/* Media */}
      {delOpen && <DeletePostDialog onPick={del} onClose={() => setDelOpen(false)} />}
      {!hideMedia && media && media.length > 0 && (
        <div className="mb-2">
          <PostMediaGallery media={media} idPrefix={id} testPrefix="post-media" />
        </div>
      )}

      {/* Footer: reaksjoner + kommentar */}
      <div className="flex items-center gap-4 mt-2 pt-2 border-t border-white/5 relative">
        {/* Reaksjons-knapp med picker */}
        <div
          className="relative"
          onMouseEnter={openPicker}
          onMouseLeave={scheduleClose}
          onTouchStart={onPressStart}
          onTouchEnd={onPressEnd}
          onTouchCancel={onPressEnd}
        >
          <button
            onClick={onHeartClick}
            className={`inline-flex items-center gap-1.5 text-xs font-meta transition-colors ${
              my_reaction ? "text-[var(--magenta)]" : "text-[var(--cream-soft)] hover:text-[var(--magenta)]"
            }`}
            data-testid={`post-like-${id}`}
          >
            {myReactionObj ? (
              <span className="text-base leading-none">{myReactionObj.emoji}</span>
            ) : (
              <Heart className="h-4 w-4" />
            )}
            {reactions_count || 0}
          </button>
          {showReactPicker && (
            <div
              className="absolute bottom-full left-0 mb-2 flex items-center gap-1 bg-[#1a1618] border border-white/10 rounded-full px-2 py-1 shadow-lg z-30"
              onMouseEnter={openPicker}
              onMouseLeave={scheduleClose}
              data-testid={`react-picker-${id}`}
            >
              {REACTIONS.map((r) => (
                <button
                  key={r.kind}
                  onClick={() => react(r.kind)}
                  className="text-xl p-1 hover:scale-125 transition-transform"
                  title={T(`ui.reaction_${r.kind}`)}
                  data-testid={`react-${r.kind}-${id}`}
                >
                  {r.emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Kommentar-knapp */}
        <button
          onClick={() => !comments_disabled && setShowComments((v) => !v)}
          disabled={comments_disabled}
          className={`inline-flex items-center gap-1.5 text-xs font-meta ${
            comments_disabled
              ? "text-[var(--cream-faint)]/40 cursor-not-allowed"
              : "text-[var(--cream-soft)] hover:text-[var(--magenta)]"
          }`}
          title={comments_disabled ? t("post.comments_off_tt", "Kommentarer er slått av") : undefined}
          data-testid={`post-comment-${id}`}
        >
          <MessageCircle className="h-4 w-4" /> {comments_count || 0}
        </button>
      </div>

      {/* Kommentar-tråd */}
      {showComments && !comments_disabled && (
        <CommentsThread postId={id} />
      )}
    </article>
  );
}


// ============================================================
// PromoBadge — vises inne i PostCard når posten har et tilbud
// ============================================================
function DeletePostDialog({ onPick, onClose }) {
  const { user } = useAuth();
  const [albums, setAlbums] = useState(null);
  const [target, setTarget] = useState("");
  const loadAlbums = async () => {
    const r = await api.get("/albums", { params: { owner: user?.username, pool: "all", limit: 100 } });
    setAlbums((r.data || []).filter((a) => !a.system_kind && !a.is_leak_album));
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="delete-post-dialog">
      <div className="w-full max-w-sm bg-[#141116] border border-white/10 rounded-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-fraunces italic text-lg text-[var(--cream)]">{T("ui.delete_post_q")}</h3>
        <p className="text-xs text-[var(--cream-soft)]">{T("ui.delete_post_media")}</p>
        <button className="btn-magenta w-full" onClick={() => onPick({ album_action: "delete_all" })} data-testid="delete-post-all">{T("ui.delete_all")}</button>
        <button className="btn-ghost w-full" onClick={() => onPick({ album_action: "keep" })} data-testid="delete-post-keep">{T("ui.keep_in_albums")}</button>
        {albums === null ? (
          <button className="btn-ghost w-full" onClick={loadAlbums} data-testid="delete-post-move">{T("ui.move_to_album")}</button>
        ) : (
          <div className="flex gap-2">
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="flex-1 bg-black/40 border border-white/15 text-sm text-[var(--cream)] px-2" data-testid="delete-post-move-select">
              <option value="">{T("ui.choose_album")}</option>
              {albums.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </select>
            <button className="btn-ghost" disabled={!target} onClick={() => onPick({ album_action: "move", target_album_id: target })} data-testid="delete-post-move-confirm">{T("ui.move")}</button>
          </div>
        )}
        <button className="text-xs text-[var(--cream-faint)] w-full" onClick={onClose} data-testid="delete-post-cancel">{T("ui.cancel")}</button>
      </div>
    </div>,
    document.body
  );
}

function PromoBadge({ promo }) {
  const [qrOpen, setQrOpen] = useState(false);
  if (!promo) return null;
  const { kind, title, body, link, discount_pct, expires_at, media = [], qr_url } = promo;
  const expired = expires_at && new Date(expires_at) < new Date();
  const Icon = kind === "percent" ? Percent : kind === "freelink" ? Gift : Sparkles;
  const label = kind === "percent" ? `${discount_pct}% RABATT`
              : kind === "freelink" ? "GRATIS"
              : "TILBUD";
  const clickable = link && !expired;

  const info = (
    <>
      <div className="flex items-center justify-between mb-1">
        <span className={`inline-flex items-center gap-1 font-meta text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
          expired ? "bg-white/10 text-[var(--cream-faint)]" : "bg-[var(--magenta)] text-white"
        }`}>
          <Icon className="h-3 w-3" strokeWidth={2.5} /> {expired ? "UTLØPT" : label}
        </span>
        {clickable && <ExternalLink className="h-3.5 w-3.5 text-[var(--magenta)]" />}
      </div>
      <div className={`font-fraunces italic text-base ${expired ? "line-through" : "text-[var(--cream)]"}`}>{title}</div>
      {body && <p className="text-xs text-[var(--cream-soft)] font-manrope mt-1 whitespace-pre-line">{body}</p>}
      {expires_at && !expired && (
        <div className="font-meta text-[9px] uppercase tracking-wider text-[var(--cream-faint)] mt-1">
          {T("ui.expires")} {new Date(expires_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </>
  );

  return (
    <div
      className={`rounded-md p-3 mb-2 border-2 ${
        expired
          ? "border-white/10 bg-white/5 text-[var(--cream-faint)]"
          : "border-[var(--magenta)] bg-gradient-to-br from-[var(--magenta)]/15 to-[var(--gold)]/10"
      }`}
      data-testid={`promo-badge-${kind}`}
    >
      {media.length > 0 && <PromoMedia media={media} dim={expired} />}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {clickable ? (
            <a href={link} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90" data-testid="promo-badge-link">
              {info}
            </a>
          ) : info}
        </div>
        {qr_url && (
          <button
            onClick={() => setQrOpen(true)}
            className="shrink-0 w-16 h-16 bg-white rounded p-1"
            title={T("ui.show_qr")}
            data-testid="promo-badge-qr"
          >
            <img src={qr_url} alt={T("ui.qr_code")} className="w-full h-full object-contain" />
          </button>
        )}
      </div>
      {qrOpen && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setQrOpen(false)}
          data-testid="promo-qr-overlay"
        >
          <img src={qr_url} alt={T("ui.qr_code")} className="max-w-[min(90vw,420px)] max-h-[80vh] bg-white rounded p-3 object-contain" />
        </div>,
        document.body
      )}
    </div>
  );
}

function PromoMedia({ media, dim }) {
  const idPrefix = (media[0]?.url || "promo").split("?")[0].split("/").pop();
  return (
    <div className={`mb-2 ${dim ? "opacity-50 grayscale" : ""}`} data-testid="promo-badge-media">
      <PostMediaGallery media={media} idPrefix={idPrefix} testPrefix="promo-media" />
    </div>
  );
}


// ============================================================
// Kommentar-tråd (flat + 1 nivå replies)
// ============================================================
function CommentsThread({ postId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // {id, username}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/profile-posts/${postId}/comments`, { params: { limit: 50 } });
      setItems(r.data?.items || []);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const payload = { text: text.trim() };
      if (replyTo?.id) payload.parent_id = replyTo.id;
      const r = await api.post(`/profile-posts/${postId}/comments`, payload);
      setItems((prev) => [r.data, ...prev]);
      setText(""); setReplyTo(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ui.send_failed"));
    }
    setSending(false);
  };

  const del = async (cid) => {
    if (!window.confirm("Slette kommentaren?")) return;
    try {
      await api.delete(`/profile-posts/comments/${cid}`);
      setItems((prev) => prev.filter((c) => c.id !== cid && c.parent_id !== cid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("ui.delete_failed"));
    }
  };

  // Flat → gruppér: top-level + replies
  const topLevel = items.filter((c) => !c.parent_id).sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
  const repliesByParent = items.filter((c) => c.parent_id).reduce((acc, c) => {
    (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
    return acc;
  }, {});

  const renderComment = (c, isReply = false) => (
    <div key={c.id} className={`flex gap-2 ${isReply ? "ml-8 mt-1" : "mt-2"}`}>
      {c.author?.avatar_url ? (
        <img src={c.author.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-[var(--magenta)]/30 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
          {(c.author?.username || "?").slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="bg-white/5 rounded-xl px-3 py-1.5 inline-block max-w-full">
          <Link to={`/profile/${c.author?.username}`} className="font-fraunces italic text-xs text-[var(--cream)] hover:text-[var(--magenta)]">
            {c.author?.display_name || c.author?.username}
          </Link>
          <p className="text-sm text-[var(--cream)] break-words">
            <LinkifyText text={c.text} />
          </p>
        </div>
        <div className="flex items-center gap-3 mt-0.5 ml-2 text-[10px] font-meta text-[var(--cream-faint)]">
          <span>{relTime(c.created_at)}</span>
          {!isReply && (
            <button
              onClick={() => setReplyTo({ id: c.id, username: c.author?.username })}
              className="hover:text-[var(--magenta)]"
              data-testid={`reply-to-${c.id}`}
            >
              {t("post.reply", "Svar")}
            </button>
          )}
          {(c.author_id === user?.id || user?.role === "admin" || user?.role === "owner") && (
            <button onClick={() => del(c.id)} className="hover:text-red-400" data-testid={`del-comment-${c.id}`}>
              {t("post.delete", "Slett")}
            </button>
          )}
        </div>
        {!isReply && repliesByParent[c.id] && repliesByParent[c.id]
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .map((r) => renderComment(r, true))
        }
      </div>
    </div>
  );

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`comments-thread-${postId}`}>
      {/* Composer */}
      <div className="flex gap-2">
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[var(--magenta)]/30 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
            {(user?.username || "?").slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          {replyTo && (
            <div className="text-[10px] font-meta text-[var(--cream-faint)] mb-1 flex items-center gap-2">
              {t("post.replying_to", "Svarer")} <span className="text-[var(--magenta)]">@{replyTo.username}</span>
              <button onClick={() => setReplyTo(null)} className="hover:text-red-400">×</button>
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("post.comment_placeholder", "Skriv en kommentar …")}
              maxLength={1000}
              className="input-editorial flex-1 text-sm px-3 py-1.5 rounded-full"
              data-testid={`comment-input-${postId}`}
            />
            <button
              onClick={send}
              disabled={sending || !text.trim()}
              className="c-btn c-btn-primary text-xs px-3"
              data-testid={`comment-send-${postId}`}
            >
              {t("post.send", "Send")}
            </button>
          </div>
        </div>
      </div>

      {/* Kommentar-liste */}
      {loading ? (
        <div className="mt-3 text-xs text-[var(--cream-faint)] font-meta">{t("post.loading", "Laster …")}</div>
      ) : topLevel.length === 0 ? (
        <div className="mt-3 text-xs text-[var(--cream-faint)]/70 italic">{t("post.no_comments", "Ingen kommentarer ennå.")}</div>
      ) : (
        <div className="mt-2">
          {topLevel.map((c) => renderComment(c))}
        </div>
      )}
    </div>
  );
}
